import { useGlobalStore } from '../stores/globalStore';

export function useSecurityVisibility() {
  const userRole = useGlobalStore((s) => s.userRole);

  return {
    // Resident grid actions. Dietech_Resident_Level follows WinForms
    // (frmUser_Roles_AddMod.vb): 1 View, 2 Add + Modify, 3 Delete,
    // 4 Clean Up Tools + Permanently Delete All. Edit and Delete were one
    // level too high here; the API gates AddMod at 2 and Delete at 3.
    canAddResident: (userRole?.dietech_Resident_Level ?? 0) >= 2,
    canEditResident: (userRole?.dietech_Resident_Level ?? 0) >= 2,
    canDeleteResident: (userRole?.dietech_Resident_Level ?? 0) >= 3,
    canDischargeResident: (userRole?.dietech_Resident_Level ?? 0) >= 2,
    // "Move Resident To...": WinForms offers it above Resident Level 1
    // (ucDietech_Resident_DataGrid.vb:223); the API checks the same.
    canMoveResident: (userRole?.dietech_Resident_Level ?? 0) >= 2,

    // Deleted (managed) residents. WinForms gates the screen and each action separately, on two
    // different role fields, and the API checks the same (PatientController):
    //  - the screen: Dietech_Managed_Residents above 0 (frmDietech_Resident_DataGrid_V2.vb:945)
    //  - Restore: Managed Residents above 1, "View, Restore" (frmDietech_Resident_Manage_DataGrid.vb:552)
    //  - Permanently Delete: Managed Residents above 2, "View, Restore, Permanently Delete" (:556)
    //  - Permanently Delete All: Dietech_Resident_Level 4, "Clean Up Tools, Permanently Delete All"
    //    (:561), the Residents field, not Managed Residents.
    canViewDeletedResidents: (userRole?.dietech_Managed_Residents ?? 0) >= 1,
    canRestoreResident: (userRole?.dietech_Managed_Residents ?? 0) >= 2,
    canPermanentlyDeleteResident: (userRole?.dietech_Managed_Residents ?? 0) >= 3,
    canPermanentlyDeleteAllResidents: (userRole?.dietech_Resident_Level ?? 0) >= 4,

    // Clinical modules - can view (level >= 1)
    canViewWeightHistory: (userRole?.dietech_Weight_History_Level ?? 0) >= 1,
    canEditWeightHistory: (userRole?.dietech_Weight_History_Level ?? 0) >= 2,
    canViewLabResults: (userRole?.dietech_Lab_Results_Level ?? 0) >= 1,
    canViewIntakeStudy: (userRole?.dietech_Intake_Study_Level ?? 0) >= 1,
    canViewLikesDislikes: (userRole?.dietech_Likes_Dislikes_Level ?? 0) >= 1,
    canEditLikesDislikes: (userRole?.dietech_Likes_Dislikes_Level ?? 0) >= 2,
    canViewMedications: (userRole?.dietech_Medications_Level ?? 0) >= 1,
    canViewNotesAssessments: (userRole?.dietech_Notes_Assessments_Level ?? 0) >= 1,
    canEditNotesAssessments: (userRole?.dietech_Notes_Assessments_Level ?? 0) >= 2,
    canViewProblemsGoals: (userRole?.dietech_Problems_Goals_Level ?? 0) >= 1,
    canViewRecommendations: (userRole?.dietech_Nutritional_Recommendation_Level ?? 0) >= 1,
    canViewSkin: (userRole?.dietech_Skin_Level ?? 0) >= 1,
    canViewCredits: (userRole?.dietech_Credit_Level ?? 0) >= 1,
    canEditCredits: (userRole?.dietech_Credit_Level ?? 0) >= 2,
    canViewCalorieProtein: (userRole?.dietech_Calorie_Protein_Level ?? 0) >= 1,
    canViewTubeFeed: (userRole?.dietech_Tube_Feed_Level ?? 0) >= 1,
    canEditTubeFeed: (userRole?.dietech_Tube_Feed_Level ?? 0) >= 2,
    canViewMealPattern: (userRole?.dietech_Meal_Pattern_Level ?? 0) >= 1,
    canEditMealPattern: (userRole?.dietech_Meal_Pattern_Level ?? 0) >= 2,
    canViewPlanOfCare: (userRole?.dietech_Plan_of_Care_Data_Entry_Level ?? 0) >= 1,
    canEditPlanOfCare: (userRole?.dietech_Plan_of_Care_Data_Entry_Level ?? 0) >= 2,
    canViewSelectiveTouch: (userRole?.dietech_Selective_Menu_Touch_Level ?? 0) >= 1,
    // WinForms offers the touch page-layout editor only when Facility_Level > 1
    // (ucDietech_Facility.vb:262); the API enforces the same on SavePage/SaveLayout.
    canEditTouchLayout: (userRole?.dietech_Facility_Level ?? 0) > 1,
    canViewReports: (userRole?.dietech_Report_Level ?? 0) >= 1,
    canViewTrayLine: (userRole?.dietech_Tray_Line_Level ?? 0) >= 1,
    canViewMenuCycle: (userRole?.dietech_Menu_Cycle_Level ?? 0) >= 1,
    // Diet fluctuations: WinForms saves above 1 (frmDietech_Flucts_AddMod.Set_Security),
    // deletes at 4 (frmDietech_Flucts_DataGrid.mbtnDelete). The API enforces both.
    canViewDietFluctuations: (userRole?.dietech_Diet_Fluctuations_Level ?? 0) >= 1,
    canEditDietFluctuations: (userRole?.dietech_Diet_Fluctuations_Level ?? 0) >= 2,
    canDeleteDietFluctuations: (userRole?.dietech_Diet_Fluctuations_Level ?? 0) >= 4,
    // Resident status history: 0 view, 1 add/modify, 2 delete (frmUser_Roles_AddMod.vb:583-590).
    // WinForms locks the date at 0 and deletes only above 1
    // (frmDietech_Resident_AddMod.vb:8710, 8763). Every level can view. The API enforces both.
    canEditStatusHistory: (userRole?.dietech_Resident_Status_History_Level ?? 0) >= 1,
    canDeleteStatusHistory: (userRole?.dietech_Resident_Status_History_Level ?? 0) >= 2,

    // EHR - thresholds from WinForms frmUser_Roles_AddMod.vb:408-419 and the EHR screens.
    // Dietech_Ehr_Level: 1 view, 2 view and apply, 3 delete, 4 reprocess all.
    canViewEHR: (userRole?.dietech_Ehr_Level ?? 0) >= 1,
    canApplyEHR: (userRole?.dietech_Ehr_Level ?? 0) >= 2,
    canDeleteEHRMessage: (userRole?.dietech_Ehr_Level ?? 0) >= 3,
    canReprocessAllEHR: (userRole?.dietech_Ehr_Level ?? 0) >= 4,
    // Mapping levels: 1 view, 2 add, 3 modify, 4 delete.
    canAddDietOrderMapping: (userRole?.dietech_Ehr_Diet_Order_Mapping_Level ?? 0) >= 2,
    canModifyDietOrderMapping: (userRole?.dietech_Ehr_Diet_Order_Mapping_Level ?? 0) >= 3,
    canAddAllergyMapping: (userRole?.dietech_Ehr_Allergy_Mapping_Level ?? 0) >= 2,
  };
}
